import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  Store,
  QrCode,
  Plus,
  Pencil,
  Trash2,
  Download,
  RefreshCw,
  Loader2,
  Users,
  Building,
  Eye,
  Copy,
  Check,
  MapPin,
  ShoppingCart,
  Footprints,
  BarChart3,
  Gift,
  Sparkles,
  ToggleLeft,
  ToggleRight,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

const retailerCategories = [
  "Food & Beverage",
  "Retail",
  "Services",
  "Entertainment",
  "Health & Beauty",
  "Other"
];

export default function AdminRetailers() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [sites, setSites] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState("all");
  
  // Dialog states
  const [showRetailerDialog, setShowRetailerDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [editingRetailer, setEditingRetailer] = useState(null);
  const [selectedQR, setSelectedQR] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  // Form states
  const [retailerForm, setRetailerForm] = useState({
    name: "",
    site_id: "",
    category: "",
    logo_base64: "",
    is_ayr_miles_partner: false
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [siteFilter, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const sitesRes = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(sitesRes.data);
      
      const retailersUrl = siteFilter === "all" 
        ? `${API}/retailers?include_inactive=true`
        : `${API}/retailers?site_id=${siteFilter}&include_inactive=true`;
      const retailersRes = await axios.get(retailersUrl, { withCredentials: true });
      setRetailers(retailersRes.data);
      
      // Fetch QR codes for all retailers
      const qrRes = await axios.get(`${API}/qr-codes?include_inactive=true`, { withCredentials: true });
      setQrCodes(qrRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRetailer = async () => {
    if (!retailerForm.name || !retailerForm.site_id) {
      toast.error("Name and site are required");
      return;
    }
    
    try {
      if (editingRetailer) {
        await axios.patch(`${API}/retailers/${editingRetailer.retailer_id}`, retailerForm, { withCredentials: true });
        toast.success("Retailer updated");
      } else {
        await axios.post(`${API}/retailers`, retailerForm, { withCredentials: true });
        toast.success("Retailer created with default QR codes");
      }
      setShowRetailerDialog(false);
      setRetailerForm({ name: "", site_id: "", category: "", logo_base64: "", is_ayr_miles_partner: false });
      setEditingRetailer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save retailer");
    }
  };

  const handleToggleAyrMiles = async (retailer) => {
    try {
      await axios.patch(`${API}/retailers/${retailer.retailer_id}`, 
        { is_ayr_miles_partner: !retailer.is_ayr_miles_partner }, 
        { withCredentials: true }
      );
      toast.success(retailer.is_ayr_miles_partner ? "Ayr Miles partnership disabled" : "Ayr Miles partnership enabled");
      fetchData();
    } catch (error) {
      toast.error("Failed to update Ayr Miles status");
    }
  };

  const handleToggleRetailer = async (retailer) => {
    try {
      await axios.patch(`${API}/retailers/${retailer.retailer_id}`, 
        { is_active: !retailer.is_active }, 
        { withCredentials: true }
      );
      toast.success(retailer.is_active ? "Retailer deactivated" : "Retailer activated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update retailer");
    }
  };

  const handleViewQRCode = async (qrCodeId) => {
    try {
      const response = await axios.get(`${API}/qr-codes/${qrCodeId}/data`, { withCredentials: true });
      setSelectedQR(response.data);
      setShowQRDialog(true);
    } catch (error) {
      toast.error("Failed to load QR code");
    }
  };

  const handleDownloadQR = async (qrCodeId, retailerName, codeType) => {
    try {
      const response = await axios.get(`${API}/qr-codes/${qrCodeId}/image`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${retailerName.replace(/\s+/g, '_')}_${codeType}.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("QR code downloaded");
    } catch (error) {
      toast.error("Failed to download QR code");
    }
  };

  const handleRegenerateQR = async (qrCodeId) => {
    if (!window.confirm("This will create a new QR code and deactivate the old one. Continue?")) return;
    
    try {
      await axios.post(`${API}/qr-codes/${qrCodeId}/regenerate`, {}, { withCredentials: true });
      toast.success("QR code regenerated");
      setShowQRDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to regenerate QR code");
    }
  };

  const handleToggleQR = async (qrCodeId, currentStatus) => {
    try {
      await axios.patch(`${API}/qr-codes/${qrCodeId}`, 
        { is_active: !currentStatus }, 
        { withCredentials: true }
      );
      toast.success(currentStatus ? "QR code deactivated" : "QR code activated");
      fetchData();
      if (selectedQR && selectedQR.qr_code_id === qrCodeId) {
        setSelectedQR({ ...selectedQR, is_active: !currentStatus });
      }
    } catch (error) {
      toast.error("Failed to update QR code");
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const openEditRetailer = (retailer) => {
    setEditingRetailer(retailer);
    setRetailerForm({
      name: retailer.name,
      site_id: retailer.site_id,
      category: retailer.category || "",
      logo_base64: retailer.logo_base64 || "",
      is_ayr_miles_partner: retailer.is_ayr_miles_partner || false
    });
    setShowRetailerDialog(true);
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.site_id === siteId);
    return site ? site.name : siteId;
  };

  const getRetailerQRCodes = (retailerId) => {
    return qrCodes.filter(qr => qr.retailer_id === retailerId);
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-retailers">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
          
          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Wallet className="w-5 h-5" /><span>Commercial Dashboard</span>
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Package className="w-5 h-5" /><span>Products & Tariffs</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Retailers
            </h1>
            <p className="text-slate-400">Manage participating retailers and QR codes</p>
          </div>
          <Button 
            onClick={() => {
              setEditingRetailer(null);
              setRetailerForm({ name: "", site_id: sites[0]?.site_id || "", category: "", logo_base64: "", is_ayr_miles_partner: false });
              setShowRetailerDialog(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-retailer-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Retailer
          </Button>
        </div>

        {/* Site Filter */}
        <div className="mb-6">
          <Select value={siteFilter} onValueChange={setSiteFilter}>
            <SelectTrigger className="w-[250px] bg-slate-900 border-slate-700 text-white" data-testid="site-filter">
              <MapPin className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by site" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Retailers List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : retailers.length === 0 ? (
          <Card className="admin-card border-slate-800">
            <CardContent className="py-12 text-center">
              <Store className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No retailers found. Add your first retailer to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {retailers.map(retailer => {
              const retailerQRs = getRetailerQRCodes(retailer.retailer_id);
              const posQR = retailerQRs.find(qr => qr.code_type === "pos" && qr.is_active);
              const footfallQR = retailerQRs.find(qr => qr.code_type === "footfall" && qr.is_active);
              
              return (
                <Card key={retailer.retailer_id} className={`admin-card border-slate-800 ${!retailer.is_active ? 'opacity-60' : ''}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {/* Logo placeholder */}
                        <div className="w-16 h-16 rounded-xl bg-slate-800 flex items-center justify-center">
                          {retailer.logo_base64 ? (
                            <img src={retailer.logo_base64} alt={retailer.name} className="w-12 h-12 object-contain" />
                          ) : (
                            <Store className="w-8 h-8 text-slate-500" />
                          )}
                        </div>
                        
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-white">{retailer.name}</h3>
                            {retailer.is_ayr_miles_partner && (
                              <span className="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                Ayr Miles
                              </span>
                            )}
                            {!retailer.is_active && (
                              <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-400">Inactive</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400">{getSiteName(retailer.site_id)}</p>
                          {retailer.category && (
                            <span className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300 mt-1 inline-block">
                              {retailer.category}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Ayr Miles Toggle */}
                        <button
                          onClick={() => handleToggleAyrMiles(retailer)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            retailer.is_ayr_miles_partner 
                              ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-300 hover:from-purple-500/30 hover:to-pink-500/30' 
                              : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                          title={retailer.is_ayr_miles_partner ? "Disable Ayr Miles Partner" : "Enable Ayr Miles Partner"}
                        >
                          {retailer.is_ayr_miles_partner ? (
                            <ToggleRight className="w-5 h-5 text-purple-400" />
                          ) : (
                            <ToggleLeft className="w-5 h-5" />
                          )}
                          <span className="hidden sm:inline">Ayr Miles Partner</span>
                        </button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditRetailer(retailer)}
                          className="text-slate-400 hover:text-white"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleRetailer(retailer)}
                          className={retailer.is_active ? "text-orange-400 hover:text-orange-300" : "text-green-400 hover:text-green-300"}
                        >
                          {retailer.is_active ? <Trash2 className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    {/* QR Codes Section */}
                    <div className="mt-4 pt-4 border-t border-slate-800">
                      <p className="text-sm text-slate-500 mb-3">QR Codes</p>
                      <div className="grid grid-cols-2 gap-4">
                        {/* POS QR */}
                        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <ShoppingCart className="w-4 h-4 text-green-400" />
                            <span className="text-sm text-slate-300">POS</span>
                            {posQR && (
                              <span className="text-xs text-slate-500">
                                ({posQR.scan_count || 0} scans)
                              </span>
                            )}
                          </div>
                          {posQR ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewQRCode(posQR.qr_code_id)}
                                className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadQR(posQR.qr_code_id, retailer.name, 'pos')}
                                className="text-slate-400 hover:text-white h-8 w-8 p-0"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">No active QR</span>
                          )}
                        </div>
                        
                        {/* Footfall QR */}
                        <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Footprints className="w-4 h-4 text-purple-400" />
                            <span className="text-sm text-slate-300">Footfall</span>
                            {footfallQR && (
                              <span className="text-xs text-slate-500">
                                ({footfallQR.scan_count || 0} scans)
                              </span>
                            )}
                          </div>
                          {footfallQR ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewQRCode(footfallQR.qr_code_id)}
                                className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadQR(footfallQR.qr_code_id, retailer.name, 'footfall')}
                                className="text-slate-400 hover:text-white h-8 w-8 p-0"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">No active QR</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Create/Edit Retailer Dialog */}
      <Dialog open={showRetailerDialog} onOpenChange={setShowRetailerDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRetailer ? "Edit Retailer" : "Add Retailer"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingRetailer 
                ? "Update retailer details"
                : "Add a new participating retailer. Default POS and Footfall QR codes will be created automatically."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={retailerForm.name}
                onChange={(e) => setRetailerForm({ ...retailerForm, name: e.target.value })}
                placeholder="e.g. Costa Coffee"
                className="bg-slate-800 border-slate-700 text-white mt-2"
              />
            </div>
            <div>
              <Label className="text-slate-300">Site *</Label>
              <Select 
                value={retailerForm.site_id} 
                onValueChange={(v) => setRetailerForm({ ...retailerForm, site_id: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-2">
                  <SelectValue placeholder="Select site" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {sites.map(site => (
                    <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Category</Label>
              <Select 
                value={retailerForm.category} 
                onValueChange={(v) => setRetailerForm({ ...retailerForm, category: v })}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-2">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {retailerCategories.map(cat => (
                    <SelectItem key={cat} value={cat} className="text-white">{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                onClick={() => setShowRetailerDialog(false)} 
                variant="outline" 
                className="flex-1 border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveRetailer} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {editingRetailer ? "Save Changes" : "Create Retailer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code View Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              QR Code Details
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View, download, or manage this QR code
            </DialogDescription>
          </DialogHeader>
          {selectedQR && (
            <div className="space-y-4 pt-4">
              {/* QR Image */}
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-xl">
                  <img 
                    src={selectedQR.image_base64} 
                    alt="QR Code" 
                    className="w-48 h-48"
                  />
                </div>
              </div>
              
              {/* Details */}
              <div className="space-y-3 bg-slate-800/50 p-4 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-slate-400">Retailer</span>
                  <span className="text-white font-medium">{selectedQR.retailer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Site</span>
                  <span className="text-white">{selectedQR.site_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Type</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedQR.code_type === 'pos' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {selectedQR.code_type === 'pos' ? 'POS' : 'Footfall'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Scans</span>
                  <span className="text-white">{selectedQR.scan_count || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Status</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    selectedQR.is_active 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {selectedQR.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              {/* Visit URL */}
              <div className="bg-slate-800/50 p-3 rounded-lg">
                <p className="text-xs text-slate-500 mb-2">Visit URL</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-blue-400 bg-slate-900 px-2 py-1 rounded truncate">
                    {selectedQR.visit_url}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedQR.visit_url)}
                    className="text-slate-400 hover:text-white h-8 w-8 p-0"
                  >
                    {copiedUrl ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={() => handleDownloadQR(selectedQR.qr_code_id, selectedQR.retailer_name, selectedQR.code_type)}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  onClick={() => handleRegenerateQR(selectedQR.qr_code_id)}
                  variant="outline"
                  className="flex-1 border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate
                </Button>
              </div>
              
              <Button
                onClick={() => handleToggleQR(selectedQR.qr_code_id, selectedQR.is_active)}
                variant="outline"
                className={`w-full ${
                  selectedQR.is_active 
                    ? 'border-red-500/50 text-red-400 hover:bg-red-500/10'
                    : 'border-green-500/50 text-green-400 hover:bg-green-500/10'
                }`}
              >
                {selectedQR.is_active ? 'Deactivate QR Code' : 'Activate QR Code'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
